import { useCallback } from 'react';

import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import type { SuggestedPromptTile } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export type UsePromptSuggestionsConfig = {
	offset?: number;
	limit?: number;
};

export type UsePromptSuggestionsState = {
	data: SuggestedPromptTile[] | null;
	isLoading: boolean;
	error: Error | null;
};

export type UsePromptSuggestionsReturn = UsePromptSuggestionsState & {
	fetchSuggestions: () => Promise<void>;
};

export const store = createStore({
	name: 'promptSuggestions',
	initialState: {
		data: null as SuggestedPromptTile[] | null,
		isLoading: false,
		error: null as Error | null,
	},
	actions: {
		reset:
			() =>
			({ setState }) => {
				setState({ data: null, isLoading: false, error: null });
			},
		getState:
			() =>
			({ getState }) =>
				getState(),
		setLoading:
			() =>
			({ setState }) => {
				setState({ isLoading: true, error: null });
			},
		setData:
			(data: SuggestedPromptTile[]) =>
			({ setState }) => {
				setState({ data, isLoading: false });
			},
		setError:
			(error: Error) =>
			({ setState }) => {
				setState({ error, isLoading: false });
			},
	},
});

const useStore = createHook(store);
const useStoreActions = createActionsHook(store);

/**
 * Subscribes to the shared prompt suggestions store. Returns data/loading/error state
 * plus a `fetchSuggestions` callback to trigger a fetch.
 */
export const usePromptSuggestions: (
	props: { config?: UsePromptSuggestionsConfig } & AssistanceServiceInputParam,
) => UsePromptSuggestionsReturn = createHookWithAssistanceService(
	({
		config = { offset: 0, limit: 9 },
		assistanceServiceInjectedParams: { assistanceService },
	}: { config?: UsePromptSuggestionsConfig } & AssistanceServiceInjectedParams) => {
		const [state] = useStore();
		const { getState, setLoading, setData, setError } = useStoreActions();

		const fetchSuggestions = useCallback(async () => {
			const { isLoading, data, error } = getState();
			if (isLoading || data || error) {
				return;
			}

			setLoading();

			const fetchOnce = () =>
				assistanceService.fetchPersonalizedPromptSuggestions({
					offset: config.offset,
					limit: config.limit,
				});

			try {
				let prompts: SuggestedPromptTile[];
				try {
					prompts = await fetchOnce();
				} catch (firstError) {
					try {
						prompts = await fetchOnce();
					} catch {
						throw firstError;
					}
				}

				if (prompts.length === 0) {
					throw new Error('No prompt suggestions returned');
				}
				setData(prompts);
			} catch (err: unknown) {
				setError(err instanceof Error ? err : new Error('Failed to fetch prompt suggestions'));
			}
		}, [config, getState, setLoading, setData, setError, assistanceService]);

		return {
			...state,
			fetchSuggestions,
		};
	},
);
